import { useState } from 'react'

/**
 * The Annotation tool's one-line text box. Opens right after the placement
 * click (ChartManager relays it as `annotation:textEdit`; EdgeChart anchors this
 * just above the label). Enter saves; Escape, or saving blank text, removes the
 * annotation. Later edits happen in the drawing's settings drawer.
 *
 * Same chrome as PlanWeightMenu (click-away backdrop, absolute box), stacked
 * above the overlay quick-edit bar (z-50): klinecharts selects the drawing on
 * the same click, so the bar opens at the same time and must not cover this.
 */
export function AnnotationTextInput({
  top,
  left,
  onCommit,
  onCancel,
}: {
  top: number
  left: number
  /** Trimmed, never blank. */
  onCommit: (text: string) => void
  onCancel: () => void
}) {
  const [text, setText] = useState('')
  const finish = () => {
    const trimmed = text.trim()
    if (trimmed) onCommit(trimmed)
    else onCancel()
  }
  return (
    <>
      {/* Click-away backdrop: keeps typed text, drops a blank annotation. */}
      <div
        style={{ position: 'absolute', inset: 0, zIndex: 51 }}
        onClick={finish}
        data-testid="annotation-text-backdrop"
      />
      <div
        // Chart chrome: a mousedown here must not deselect the drawing.
        data-chart-chrome=""
        data-testid="annotation-text-input"
        style={{
          position: 'absolute',
          top,
          left,
          zIndex: 52,
          padding: 3,
          borderRadius: 6,
          background: '#1e222d',
          border: '1px solid #2a2e39',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}
      >
        <input
          autoFocus
          aria-label="Annotation text"
          placeholder="Add text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Every key belongs to this box: never the chart's Delete /
            // Backspace, hotkeys, type-to-search or Escape layers.
            e.stopPropagation()
            if (e.key === 'Enter') {
              if (e.nativeEvent.isComposing) return
              e.preventDefault()
              finish()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onCancel()
            }
          }}
          style={{
            width: 160,
            padding: '3px 6px',
            borderRadius: 4,
            fontSize: 11,
            color: '#d1d4dc',
            background: '#131722',
            border: '1px solid #2a2e39',
            outline: 'none',
          }}
        />
      </div>
    </>
  )
}
